import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RppToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  className?: string;
  /** Grey out the switch (e.g. national pages where RPP = 100 changes nothing). */
  disabled?: boolean;
  /** Shown in the tooltip when disabled, instead of the standard explanation. */
  disabledReason?: string;
}

// Real-wage (cost-of-living adjusted) toggle. This is one of WageAtlas's key
// differentiators versus raw BLS tables, so it is free for every visitor.
const RppToggle = ({
  enabled,
  onToggle,
  className = '',
  disabled = false,
  disabledReason,
}: RppToggleProps) => {
  return (
    <TooltipProvider>
      <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${disabled ? 'opacity-60' : ''} ${className}`}>
        <Switch
          id="rpp-toggle"
          checked={disabled ? false : enabled}
          onCheckedChange={onToggle}
          disabled={disabled}
          className="shrink-0 data-[state=checked]:bg-wage-secondary"
        />
        <Label
          htmlFor="rpp-toggle"
          className={`text-sm font-medium flex items-center gap-1.5 sm:gap-2 min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className="truncate">
            <span className="sm:hidden">Real Wages</span>
            <span className="hidden sm:inline">Real Wages (Purchasing Power)</span>
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground hover:text-foreground transition-colors" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">
                {disabled && disabledReason
                  ? disabledReason
                  : 'Adjusts salaries for local cost of living using Regional Price Parity (RPP) data. Shows what your money is actually worth in different areas.'}
              </p>
              <p className="text-sm mt-1.5">
                <Link to="/real-wages/" className="underline underline-offset-2">
                  Learn how real wages work
                </Link>
              </p>
            </TooltipContent>
          </Tooltip>
        </Label>
      </div>
    </TooltipProvider>
  );
};

export default RppToggle;
